import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import VerticalDotsMenu from 'common/VerticalDotsMenu';
import { map } from 'lodash';
import './RowActionsMenu.scss';

export const MENU_BELOW = 'bottom-end';
export const MENU_ABOVE = 'top-end';

function nearestScrollParent(element) {
  let node = element && element.parentElement;

  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
    node = node.parentElement;
  }

  return null;
}

function preferredPosition(wrapper) {
  if (!wrapper) {
    return MENU_BELOW;
  }

  const anchor = wrapper.querySelector('.ui-menu__anchor');
  const list = wrapper.querySelector('.ui-menu__list');
  if (!anchor || !list) {
    return MENU_BELOW;
  }

  const menuHeight = list.offsetHeight;
  const anchorRect = anchor.getBoundingClientRect();
  const scrollParent = nearestScrollParent(wrapper);
  const bounds = scrollParent ?
    scrollParent.getBoundingClientRect() :
    { top: 0, bottom: window.innerHeight };

  const fitsBelow = anchorRect.bottom + menuHeight <= bounds.bottom;
  const fitsAbove = anchorRect.top - menuHeight >= bounds.top;

  return !fitsBelow && fitsAbove ? MENU_ABOVE : MENU_BELOW;
}

const RowActionsMenu = ({ actions, data }) => {
  const wrapperRef = useRef(null);
  const [position, setPosition] = useState(MENU_BELOW);

  const choosePosition = useCallback(() => {
    setPosition(preferredPosition(wrapperRef.current));
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) {
      return undefined;
    }
    el.addEventListener('mousedown', choosePosition);
    el.addEventListener('keydown', choosePosition);

    return () => {
      el.removeEventListener('mousedown', choosePosition);
      el.removeEventListener('keydown', choosePosition);
    };
  }, [choosePosition]);

  const menuItems = map(actions, (menuItem = {}) => ({
    label: menuItem.label,
    onSelect: () => menuItem.action(data),
  }));

  return (
    <div ref={wrapperRef}>
      <VerticalDotsMenu
        className="row-actions-menu"
        id="vertical-dots-menu"
        menuItems={menuItems}
        position={position}
      />
    </div>
  );
};

RowActionsMenu.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({
    action: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired),
  data: PropTypes.object,
};

RowActionsMenu.defaultProps = {
  actions: [],
  data: {},
};

export default RowActionsMenu;
